import { useAuth } from "app/hooks";
import { useTheme } from "app/shared";
import HeaderConnect from "app/widgets/crypto-header/header-connect";
import { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useGetTotalBalanceQuery } from "store/api/new.api";
import styles from "./timer.module.scss";

interface RequirementsCountdownWidgetProps {
  whitelistStartDate: Date;
  whitelistEndDate: Date;
  sellStartDate: Date;
  sellEndDate: Date;
  totalTokens?: number;
}

const NotLoggedTimer: FC<RequirementsCountdownWidgetProps> = ({
  whitelistStartDate,
  whitelistEndDate,
  totalTokens,
  sellStartDate,
  sellEndDate,
}) => {
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  }>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [timerText, setTimerText] = useState("");
  const { theme } = useTheme();

  const isAuth = useAuth();

  const { idoId } = useParams<{ idoId: string }>();
  const { data: totalBalance } = useGetTotalBalanceQuery({ idoId });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const currentTime = new Date();
      let targetDate: Date;

      if (currentTime < whitelistStartDate) {
        targetDate = whitelistStartDate;
        setTimerText("SmartWhitelist Start In:");
      } else if (
        currentTime >= whitelistStartDate &&
        currentTime < whitelistEndDate
      ) {
        targetDate = whitelistEndDate;
        setTimerText("SmartWhitelist Ends In:");
      } else if (
        currentTime >= whitelistEndDate &&
        currentTime < sellStartDate
      ) {
        targetDate = sellStartDate;
        setTimerText("IDO Start In");
      } else if (currentTime >= sellStartDate && currentTime < sellEndDate) {
        targetDate = sellEndDate;
        setTimerText("IDO Time Left:");
      } else if (totalTokens && totalBalance && totalTokens <= totalBalance) {
        targetDate = new Date();
        setTimerText("Sold Out");
      } else {
        targetDate = new Date();
        setTimerText("Sold Out");
      }

      const difference = targetDate.getTime() - currentTime.getTime();
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / (1000 * 60)) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [whitelistStartDate, whitelistEndDate, sellStartDate, sellEndDate]);

  return (
    <div
      className={`${styles.widget} ${theme === "dark" ? styles.darkTheme : ""}`}
    >
      <div className={styles.wrapper}>
        <div>
          <div className={styles.timer}>
            <h3 className={styles.title}>{timerText}</h3>
            <div className={styles.time}>
              <div>
                <span className={styles.time1}>
                  {String(timeLeft.days).padStart(2, "0")}
                </span>
                <span
                  className={`${styles.time2} ${
                    theme === "dark" ? styles.darkTheme : ""
                  }`}
                >
                  DAYS
                </span>
              </div>
              <div>
                <span className={styles.time1}>
                  {String(timeLeft.hours).padStart(2, "0")}
                </span>
                <span
                  className={`${styles.time2} ${
                    theme === "dark" ? styles.darkTheme : ""
                  }`}
                >
                  HOURS
                </span>
              </div>
              <div>
                <span className={styles.time1}>
                  {String(timeLeft.minutes).padStart(2, "0")}
                </span>
                <span
                  className={`${styles.time2} ${
                    theme === "dark" ? styles.darkTheme : ""
                  }`}
                >
                  MINUTES
                </span>
              </div>
              <div>
                <span className={styles.time1}>
                  {String(timeLeft.seconds).padStart(2, "0")}
                </span>
                <span
                  className={`${styles.time2} ${
                    theme === "dark" ? styles.darkTheme : ""
                  }`}
                >
                  SECONDS
                </span>
              </div>
            </div>
          </div>
          {!isAuth && (
            <div className={styles.connector}>
              <HeaderConnect />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotLoggedTimer;
